import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { resourcesService } from '../../services/resources.service';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import LoadingState from '../../components/ui/LoadingState';

const { FiArrowLeft, FiClock, FiCheckCircle, FiShare2, FiBookmark, FiAlertTriangle } = FiIcons;

export default function ResourceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { contentLanguage, t } = useTranslation();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchResource() {
      try {
        const data = await resourcesService.getResourceBySlug(slug);
        setResource(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchResource();
  }, [slug]);

  if (loading) return <LoadingState />;
  if (!resource) return <div className="p-12 text-center">Resource not found</div>;

  const title = resource.title[contentLanguage] || resource.title['en'] || resource.title['el'];
  const content = resource.content?.[contentLanguage] || resource.content?.['en'] || resource.content?.['el'] || 'Content not available in this language.';
  const isFallback = !resource.title[contentLanguage];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-8"
      >
        <SafeIcon icon={FiArrowLeft} />
        <span>{contentLanguage === 'el' ? 'Επιστροφή' : 'Back'}</span>
      </button>

      {isFallback && (
        <div className="mb-8 p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3 text-amber-800 text-sm">
          <SafeIcon icon={FiAlertTriangle} className="mt-0.5 flex-shrink-0" />
          <p>
            {contentLanguage === 'el' 
              ? 'Αυτό το περιεχόμενο δεν είναι ακόμη διαθέσιμο στα Ελληνικά. Εμφανίζεται η αγγλική έκδοση.' 
              : 'This content is not yet available in English. Showing fallback version.'}
          </p>
        </div>
      )}

      <header className="mb-12">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {resource.trust_level === 'medically_reviewed' && (
            <div className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-full border border-green-100 uppercase tracking-tighter">
              <SafeIcon icon={FiCheckCircle} />
              <span>{contentLanguage === 'el' ? 'Ιατρικά Ελεγμένο' : 'Medically Reviewed'}</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-gray-500 text-sm">
            <SafeIcon icon={FiClock} />
            <span>{contentLanguage === 'el' ? 'Επαληθεύτηκε:' : 'Verified:'} {resource.last_verified}</span>
          </div>
        </div>

        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
          {title}
        </h1>

        <div className="flex items-center justify-between border-y border-gray-100 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
              <SafeIcon icon={FiCheckCircle} />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{resource.author || 'HAHUB Editorial'}</p>
              <p className="text-xs text-gray-500">{contentLanguage === 'el' ? 'Επιστημονική Ομάδα' : 'Scientific Team'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="p-2 text-gray-400 hover:text-gray-900 transition-colors">
              <SafeIcon icon={FiBookmark} className="text-xl" />
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-900 transition-colors">
              <SafeIcon icon={FiShare2} className="text-xl" />
            </button>
          </div>
        </div>
      </header>

      <article className="prose prose-lg prose-blue max-w-none">
        {/* Simple markdown-like renderer for mock data */}
        {content.split('\n').map((line, i) => {
          if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold mt-8 mb-4">{line.replace('## ', '')}</h2>;
          if (line.startsWith('### ')) return <h3 key={i} className="text-xl font-bold mt-6 mb-3">{line.replace('### ', '')}</h3>;
          if (line.startsWith('* ')) return <li key={i} className="ml-4 mb-2">{line.replace('* ', '')}</li>;
          if (line.trim() === '') return <br key={i} />;
          return <p key={i} className="text-gray-700 leading-relaxed mb-4">{line}</p>;
        })}
      </article>

      <footer className="mt-16 pt-12 border-t border-gray-100">
        <div className="bg-gray-50 rounded-2xl p-8 text-center">
          <h4 className="text-lg font-bold text-gray-900 mb-2">
            {contentLanguage === 'el' ? 'Βρήκατε αυτόν τον πόρο χρήσιμο;' : 'Did you find this resource helpful?'}
          </h4>
          <p className="text-gray-600 mb-6">
            {contentLanguage === 'el' ? 'Η γνώμη σας μας βοηθά να βελτιωθούμε.' : 'Your feedback helps us improve.'}
          </p>
          <div className="flex justify-center gap-4">
            <button className="px-6 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors font-medium">
              {contentLanguage === 'el' ? 'Ναι' : 'Yes'}
            </button>
            <button className="px-6 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors font-medium">
              {contentLanguage === 'el' ? 'Όχι' : 'No'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}